import React from 'react';
import {
  Badge,
  Box,
  Flex,
  Icon,
  IconButton,
  Text,
  Tooltip,
  useColorModeValue,
  useDisclosure,
} from '@chakra-ui/react';
import Card from 'components/card/Card';
import { MdAdd, MdLocalPharmacy } from 'react-icons/md';
import { usePatologicos } from 'hooks/useAntecedentes';
import { Patient } from 'types/models';
import AddAntecedentesAlergiaModal from './AddAntecedentesAlergiaModal';

type AlergiaRaw = { descripcion?: string; medicamento?: string; reaccion?: string };

interface AlergiasSummaryPanelProps {
  patient: Patient;
  [x: string]: any;
}

export default function AlergiasSummaryPanel({ patient, ...rest }: AlergiasSummaryPanelProps) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { data: patologicos } = usePatologicos(patient.id);
  const textColor  = useColorModeValue('secondaryGray.900', 'white');
  const mutedColor = useColorModeValue('secondaryGray.500', 'secondaryGray.400');
  const emptyBg   = useColorModeValue('gray.50', 'navy.700');

  const alergias = ((patologicos?.alergiasMedicamentos ?? []) as AlergiaRaw[]);

  return (
    <>
      <Card p='20px' {...rest}>
        <Flex align='center' justify='space-between' mb='14px'>
          <Flex align='center' gap='8px'>
            <Flex
              w='26px' h='26px' bg='orange.50' borderRadius='8px'
              align='center' justify='center' flexShrink={0}>
              <Icon as={MdLocalPharmacy} color='orange.400' w='13px' h='13px' />
            </Flex>
            <Text color={textColor} fontSize='sm' fontWeight='800'>
              Alergias
            </Text>
          </Flex>
          <Tooltip label='Agregar alergia' placement='top' hasArrow>
            <IconButton
              aria-label='Agregar alergia'
              icon={<Icon as={MdAdd} />}
              size='sm'
              variant='outline'
              colorScheme='orange'
              borderRadius='full'
              onClick={onOpen}
            />
          </Tooltip>
        </Flex>

        {alergias.length === 0 ? (
          <Flex
            direction='column' align='center' justify='center'
            gap='8px' py='32px' bg={emptyBg} borderRadius='12px'>
            <Icon as={MdLocalPharmacy} color='secondaryGray.300' w='28px' h='28px' />
            <Text fontSize='sm' color={mutedColor} fontWeight='600'>
              Sin alergias registradas
            </Text>
            <Text fontSize='xs' color={mutedColor}>
              Haz clic en "+" para agregar
            </Text>
          </Flex>
        ) : (
          <Flex direction='column' gap='8px'>
            {alergias.map((a, idx) => (
              <Box key={idx}>
                <Badge
                  colorScheme='orange'
                  borderRadius='full'
                  px='10px' py='4px'
                  fontSize='xs' fontWeight='600'>
                  {a.descripcion ?? a.medicamento}
                </Badge>
                {a.reaccion && (
                  <Text fontSize='10px' color={mutedColor} mt='2px' ms='2px'>
                    {a.reaccion}
                  </Text>
                )}
              </Box>
            ))}
          </Flex>
        )}
      </Card>

      <AddAntecedentesAlergiaModal
        isOpen={isOpen}
        onClose={onClose}
        patientId={patient.id}
        initialTab={1}
      />
    </>
  );
}
